import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import { Box } from '@mui/material';
import { Button, Form, Tooltip } from 'antd';
import Notification01Icon from 'assets/icons/notification01Icon';
import { useLocation } from 'react-router-dom';
import { useMemo, useState, useEffect } from 'react';
import LeaseV9JournalModal from './LeaseV9JournalModal';
import DataTable from 'components/LynDataTable/DataTable';
import { useSendControlDataMutation } from 'store/features/api/apiSlice';
import ConfirmModal from 'components/modal/ConfirmModal';
import { formatNumber, utcConverter } from '../../lease-record-management-v9-new-version/utils/helperFunc';

const styles = `
  .active .MuiTableCell-root:first-of-type {
    background-color: inherit !important;
  }
`;

const hasMeaningfulValue = (value) => {
	if (value === undefined || value === null) return false;
	if (dayjs.isDayjs(value)) return value.isValid();
	if (typeof value === 'number') return !Number.isNaN(value);
	if (typeof value === 'string') {
		const trimmedValue = value.trim();
		return trimmedValue !== '' && trimmedValue !== 'undefined' && trimmedValue !== 'null' && trimmedValue !== 'NaN';
	}
	if (Array.isArray(value)) return value.length > 0;
	return true;
};

const pickFirstMeaningfulValue = (...values) => values.find((value) => hasMeaningfulValue(value));

const getNormalizedLeaseData = (viewData) => {
	if (viewData?.parkedData && !isEmpty(viewData?.parkedData)) {
		return viewData.parkedData;
	}

	if (viewData?.data && !isEmpty(viewData?.data)) {
		return viewData.data;
	}

	return viewData ?? {};
};

const LeaseV9NewTrailModeScheduleTable = ({ viewData, isClicked, queryV3Loader, screen = 'CREATE', leaseControlData = [] }) => {
	const status = window.leaseActivationStatus;

	const { state } = useLocation();
	const form = Form.useFormInstance();
	const leaseId = form.getFieldValue('leaseId');
	const leaseType = form.getFieldValue('leaseType');
	const [isActionColumnShowAllow, setIsActionColumnShowAllow] = useState(false);

	const [getJournalEntryData, { data: journalEntryData, isLoading: isJournalEntryDataLoading }] = useSendControlDataMutation();
	const [getActionRequiredData] = useSendControlDataMutation();

	const [rowId, setRowId] = useState(null);
	const [showJournalEntryModal, setShowJournalEntryModal] = useState(false);
	const [tableUpperLeftExtraInfo, setTableUpperLeftExtraInfo] = useState({});
	const [actionRequiredConfirmModal, setActionRequiredConfirmModal] = useState(false);
	const normalizedLeaseData = useMemo(() => getNormalizedLeaseData(viewData), [viewData]);

	useEffect(() => {
		const styleElement = document.createElement('style');

		styleElement.innerHTML = styles;
		document.head.appendChild(styleElement);

		return () => {
			document.head.removeChild(styleElement);
		};
	}, []);

	const handleEyeButton = async (row) => {
		const index = row.id;
		const formData = form.getFieldsValue();
		const resolvedLeaseTitle = pickFirstMeaningfulValue(formData?.leaseTitle, normalizedLeaseData?.leaseTitle, '');
		const resolvedLeaseId = pickFirstMeaningfulValue(formData?.leaseId, normalizedLeaseData?.leaseId);
		const resolvedLeaseType = pickFirstMeaningfulValue(formData?.leaseType, normalizedLeaseData?.leaseType);
		const resolvedLessorName = pickFirstMeaningfulValue(formData?.lessorName, normalizedLeaseData?.lessorName);
		const resolvedLessorID = pickFirstMeaningfulValue(formData?.lessorID, normalizedLeaseData?.lessorID);
		const resolvedLeaseTerm = pickFirstMeaningfulValue(formData?.leaseTerm, normalizedLeaseData?.leaseTerm);
		const resolvedPaymentFrequency = pickFirstMeaningfulValue(formData?.paymentFrequency, normalizedLeaseData?.paymentFrequency);
		const resolvedPaymentModality = pickFirstMeaningfulValue(formData?.paymentModality, normalizedLeaseData?.paymentModality);
		const resolvedDiscountRate = pickFirstMeaningfulValue(formData?.initialDiscountRate, normalizedLeaseData?.initialDiscountRate);
		const resolvedAdvanceAmounts = pickFirstMeaningfulValue(formData?.advanceAmounts, normalizedLeaseData?.advanceAmounts, []);
		const resolvedGrossRents = pickFirstMeaningfulValue(formData?.grossRents, normalizedLeaseData?.grossRents, []);
		const resolvedAdvanceAmount = pickFirstMeaningfulValue(
			formData?.advanceAmount,
			normalizedLeaseData?.advanceAmount,
			Array.isArray(resolvedAdvanceAmounts) ? resolvedAdvanceAmounts.reduce((acc, curr) => acc + (Number(curr) || 0), 0) : 0
		);
		const resolvedAdvanceDuration = pickFirstMeaningfulValue(
			formData?.advanceDuration,
			normalizedLeaseData?.advanceDuration,
			Array.isArray(resolvedAdvanceAmounts) ? resolvedAdvanceAmounts.filter((item) => Number(item) !== 0).length : 0
		);
		const resolvedUpfrontLeasePayment = pickFirstMeaningfulValue(
			formData?.upfrontLeasePayment,
			normalizedLeaseData?.upfrontLeasePayment,
			0
		);
		const resolvedInitialDirectCost = pickFirstMeaningfulValue(
			formData?.initialDirectCost,
			normalizedLeaseData?.initialDirectCost,
			0
		);
		const resolvedSecurityDeposit = pickFirstMeaningfulValue(
			formData?.securityDeposit,
			normalizedLeaseData?.securityDeposit,
			0
		);
		const resolvedCashIncentiveAmount = pickFirstMeaningfulValue(
			formData?.cashIncentiveAmount,
			normalizedLeaseData?.cashIncentiveAmount,
			normalizedLeaseData?.cashIncentive,
			0
		);
		const resolvedEndOfFinancialYear = pickFirstMeaningfulValue(
			formData?.endOfFinancialYear,
			normalizedLeaseData?.endOfFinancialYear
		);
		const resolvedLeaseStartDate = pickFirstMeaningfulValue(formData?.leaseStartDate, normalizedLeaseData?.leaseStartDate);
		const resolvedLeaseEndDate = pickFirstMeaningfulValue(formData?.leaseEndDate, normalizedLeaseData?.leaseEndDate);
		const resolvedGrossRentList = pickFirstMeaningfulValue(formData?.grossRentList, normalizedLeaseData?.grossRentList, []);
		const resolvedAdvanceAmountList = pickFirstMeaningfulValue(formData?.advanceAmountList, normalizedLeaseData?.advanceAmountList, []);
		const resolvedGrossRentListSet = pickFirstMeaningfulValue(formData?.grossRentListSet, normalizedLeaseData?.grossRentListSet, []);
		const resolvedAdvanceAmountListSet = pickFirstMeaningfulValue(
			formData?.advanceAmountListSet,
			normalizedLeaseData?.advanceAmountListSet,
			[]
		);
		const resolvedChanges = pickFirstMeaningfulValue(formData?.changes, normalizedLeaseData?.changes, []);
		const resolvedVersion = pickFirstMeaningfulValue(formData?.version, normalizedLeaseData?.version);
		const resolvedIdentityId = pickFirstMeaningfulValue(formData?.identityId, normalizedLeaseData?.identityId);
		const resolvedEffectiveDateHistory = pickFirstMeaningfulValue(
			formData?.effectiveDateHistory,
			normalizedLeaseData?.effectiveDateHistory,
			[]
		);
		const resolvedModificationSequence = pickFirstMeaningfulValue(
			formData?.modificationSequence,
			normalizedLeaseData?.modificationSequence
		);
		const resolvedLeaseStartDateDayjs = dayjs(resolvedLeaseStartDate);
		const resolvedLeaseEndDateDayjs = dayjs(resolvedLeaseEndDate);
		const resolvedEndOfFinancialYearDayjs = dayjs(resolvedEndOfFinancialYear);

		setTableUpperLeftExtraInfo(
			screen?.toLowerCase() === 'create'
				? {
						leaseTitle: resolvedLeaseTitle,
						leasePeriod: index + 1,
					}
				: {
						leaseId: resolvedLeaseId,
						leaseTitle: resolvedLeaseTitle,
						leasePeriod: index + 1,
					}
		);

		let calculateData;
		let identityData;

		if (screen?.toLowerCase() === 'create' || (screen?.toLowerCase() === 'revise' && formData?.modificationSequence === 0)) {
			calculateData = {
				advanceAmount: resolvedAdvanceAmount,
				leaseTerm: resolvedLeaseTerm,
				discountRate: resolvedDiscountRate,
				startYear: String(resolvedLeaseStartDateDayjs?.year()),
				escalation: 0,
				startMonth: String(resolvedLeaseStartDateDayjs?.month() + 1),
				paymentFrequency: resolvedPaymentFrequency,
				paymentTime: resolvedPaymentModality,
				advanceDuration: resolvedAdvanceDuration,
				startDay: String(resolvedLeaseStartDateDayjs?.date()),
				upfrontLeasePayment: String(resolvedUpfrontLeasePayment),
				initialDirectCost: String(resolvedInitialDirectCost),
				securityDeposit: String(resolvedSecurityDeposit),
				endOfFinancialYear: resolvedEndOfFinancialYear,
				advanceAmounts: resolvedAdvanceAmounts,
				grossRents: resolvedGrossRents,
				changes: [],
				grossRent: 0,
				cashIncentiveAmount: String(resolvedCashIncentiveAmount ?? 0),
			};
			identityData = {
				...normalizedLeaseData,
				...formData,
				version: resolvedVersion,
				identityId: resolvedIdentityId,
				lessorID: resolvedLessorID,
				tempLeaseId: resolvedLeaseId,
				leaseId: resolvedLeaseId,
				leaseStartDate: utcConverter(resolvedLeaseStartDateDayjs),
				leaseTitle: resolvedLeaseTitle,
				lessorName: resolvedLessorName,
				leaseTerm: resolvedLeaseTerm,
				leaseEndDate: utcConverter(resolvedLeaseEndDateDayjs),
				paymentModality: resolvedPaymentModality,
				leaseType: resolvedLeaseType,
				initialDiscountRate: resolvedDiscountRate,
				grossRentList: resolvedGrossRentList,
				paymentFrequency: resolvedPaymentFrequency,
				advanceAmountList: resolvedAdvanceAmountList,
				advanceAmount: resolvedAdvanceAmount,
				advanceAmounts: resolvedAdvanceAmounts,
				upfrontLeasePayment: String(resolvedUpfrontLeasePayment),
				initialDirectCost: String(resolvedInitialDirectCost),
				securityDeposit: String(resolvedSecurityDeposit),
				grossRents: resolvedGrossRents,
				grossRent: 0,
				escalation: 0,
				cashIncentiveAmount: String(resolvedCashIncentiveAmount ?? 0),
				changes: resolvedChanges?.map((item) => {
					return {
						...item,
						cashIncentiveAmount: String(resolvedCashIncentiveAmount ?? 0),
						inputChangeAdvanceAmounts: resolvedAdvanceAmounts,
						inputChangeGrossRents: resolvedGrossRents,
						inputChangeAdvance: Array.isArray(resolvedAdvanceAmounts)
							? resolvedAdvanceAmounts.reduce((acc, curr) => acc + (Number(curr) || 0), 0)
							: 0,
						initialDirectCost: String(resolvedInitialDirectCost ?? 0),
						securityDeposit: String(resolvedSecurityDeposit ?? 0),
						upfrontLeasePayment: String(resolvedUpfrontLeasePayment ?? 0),
					};
				}),
				modifiedDateTime: utcConverter(dayjs()),
				paymentModalityLabel: resolvedPaymentModality?.toLowerCase() === 'start' ? 'Advance basis' : 'Acrual Basis',
				endOfFinancialYear: utcConverter(resolvedEndOfFinancialYearDayjs),
				lastModifiedDate: utcConverter(dayjs()),
				grossRentListSet: resolvedGrossRentListSet,
				advanceAmountListSet: resolvedAdvanceAmountListSet,
				advanceDuration: resolvedAdvanceDuration,
				effectiveDateHistory: resolvedEffectiveDateHistory,
				modificationSequence: resolvedModificationSequence,
			};

			const payloadData = {
				calculateData,
				identityData,
				scheduledIndex: index + 1,
			};

			const convertJson = JSON.stringify(payloadData);
			const convertBase64 = btoa(convertJson);

			const formattedPayload = {
				data: convertBase64,
				processCode: state?.processCode,
				scriptName: 'lease-controller-schedule-findata2',
				payloadData: payloadData,
			};

			try {
				setTimeout(() => {
					setShowJournalEntryModal(true);
				}, 500);
				await getJournalEntryData(formattedPayload);
			} catch (error) {
				console.error('Error in handleEyeButton:', error);
			}
		} else {
			const effectiveChanges = form.getFieldValue('changes')?.filter((_, index) => index !== 0);

			calculateData = {
				advanceAmount:
					formData?.changes?.[0]?.inputChangeAdvanceAmounts?.reduce((accumulator, currentValue) => accumulator + currentValue, 0) ?? 0,
				leaseTerm: formData?.changes?.[0]?.inputChangeLeaseTerm,
				discountRate: formData?.changes?.[0]?.inputChangeDiscountRate,
				startDay: String(formData?.leaseStartDate?.date()),
				startMonth: String(formData?.leaseStartDate?.month() + 1),
				startYear: String(formData?.leaseStartDate?.year()),
				escalation: 0,
				paymentFrequency: formData?.changes?.[0]?.paymentFrequency,
				paymentTime: formData?.changes?.[0]?.paymentTime,
				advanceDuration: formData?.changes?.[0]?.inputChangeAdvanceAmounts?.filter((item) => item !== 0)?.length,
				upfrontLeasePayment: String(formData?.changes?.[0]?.upfrontLeasePayment),
				cashIncentiveAmount: String(formData?.cashIncentiveAmount ?? 0),
				initialDirectCost: String(formData?.changes?.[0]?.initialDirectCost),
				securityDeposit: String(formData?.securityDeposit),
				endOfFinancialYear: formData?.endOfFinancialYear,
				advanceAmounts: formData?.changes?.[0]?.inputChangeAdvanceAmounts,
				grossRents: formData?.changes?.[0]?.inputChangeGrossRents,
				changes: effectiveChanges.map((item, index) => {
					if (index === effectiveChanges.length - 1) {
						return {
							...item,
							cashIncentiveAmount: String(formData?.cashIncentiveAmount ?? 0),
							initialDirectCost: String(formData?.initialDirectCost ?? 0),
							securityDeposit: String(formData?.securityDeposit ?? 0),
							upfrontLeasePayment: String(formData?.upfrontLeasePayment ?? 0),
						};
					}
					return {
						...item,
					};
				}),
				grossRent: 0,
			};

			const { changes, leaseId, ...rest } = formData;

			const leaseIdRefactored =
				!isClicked && ['active', 'closed', 'inactive'].includes(status?.toLowerCase()) ? { leaseId } : { tempLeaseId: leaseId };

			identityData = {
				...rest,
				...leaseIdRefactored,
				changes: changes.map((item) => {
					return {
						...item,
						securityDeposit: String(formData?.securityDeposit ?? 0),
						initialDirectCost: String(formData?.initialDirectCost ?? 0),
						cashIncentiveAmount: String(formData?.cashIncentiveAmount ?? 0),
						upfrontLeasePayment: String(formData?.upfrontLeasePayment ?? 0),
						inputChangeAdvance: item?.inputChangeAdvanceAmounts?.reduce((acc, curr) => acc + curr, 0),
					};
				}),
			};
			const payloadData = {
				calculateData,
				identityData: {
					...identityData,
					cashIncentiveAmount: String(formData?.cashIncentiveAmount ?? 0),
					initialDirectCost: String(formData?.initialDirectCost ?? 0),
					securityDeposit: String(formData?.securityDeposit ?? 0),
					upfrontLeasePayment: String(formData?.upfrontLeasePayment ?? 0),
				},
				scheduledIndex: index + 1,
				calculate: false,
			};

			const convertJson = JSON.stringify(payloadData);
			const convertBase64 = btoa(convertJson);

			const formattedPayload = {
				data: convertBase64,
				payloadData: payloadData,
				processCode: state?.processCode,
				scriptName: 'lease-controller-schedule-findata2',
			};

			try {
				setTimeout(() => {
					setShowJournalEntryModal(true);
				}, 500);

				await getJournalEntryData(formattedPayload);
			} catch (error) {
				console.error('Error in handleEyeButton:', error);
			}
		}
	};

	const handleActionRequiredButton = async () => {
		const payloaddata = {
			id: rowId,
		};
		const convertJson = JSON.stringify(payloaddata);
		const convertBase64 = btoa(convertJson);
		const formattedPayload = {
			data: convertBase64,
			processCode: state?.processCode,
			scriptName: 'lease-controller-schedule-ready',
			payloadData: payloaddata,
		};

		try {
			await getActionRequiredData(formattedPayload);

			if (typeof queryV3Loader === 'function') {
				await queryV3Loader(true);
			}
		} catch (error) {
			console.error('Error in handleActionRequiredButton:', error);
		}
	};

	const columnData = useMemo(() => {
		if (leaseType === 'Operating Lease') {
			return [
				{
					id: 'date',
					width: '5%',
					align: 'center',
					accessor: 'date',
					cellAlign: 'center',
					Header: 'Date',
				},
				{
					id: 'period',
					width: '5%',
					align: 'center',
					accessor: 'period',
					cellAlign: 'center',
					Header: 'Period',
				},
				{
					id: 'grossRent',
					width: '5%',
					align: 'center',
					accessor: 'grossRent',
					cellAlign: 'center',
					Header: 'Gross Pay',
				},
				{
					id: 'advanceAmount',
					width: '5%',
					align: 'center',
					accessor: 'advanceAmount',
					cellAlign: 'center',
					Header: 'Advance',
				},
				{
					id: 'netPay',
					width: '5%',
					align: 'center',
					accessor: 'netPay',
					cellAlign: 'center',
					Header: 'Net Pay',
				},
				{
					id: 'payment',
					width: '5%',
					align: 'right',
					accessor: 'payment',
					cellAlign: 'right',
					Header: 'Payment',
				},
				...(isActionColumnShowAllow
					? [
							{
								Header: 'Action',
								Cell: (cell) => {
									if (cell?.row?.original?.processStatus?.toLowerCase() === 'returned') {
										return (
											<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
												<div style={{ textAlign: 'center' }}>
													<Tooltip title="Action Required">
														<Button
															type="text"
															icon={<Notification01Icon width={16} height={16} color="#B00020" />}
															onClick={(e) => {
																e.stopPropagation();
																setRowId(cell?.row?.original?._id);
																setActionRequiredConfirmModal(true);
															}}
														></Button>
													</Tooltip>
												</div>
											</Box>
										);
									}
									return null;
								},
								disableSortBy: 'true',
								align: 'center',
							},
						]
					: []),
			];
		} else {
			return [
				{
					id: 'date',
					width: '5%',
					align: 'center',
					accessor: 'date',
					cellAlign: 'center',
					Header: 'Date',
				},
				{
					id: 'period',
					width: '5%',
					align: 'center',
					accessor: 'period',
					cellAlign: 'center',
					Header: 'Period',
				},
				{
					id: 'grossRent',
					width: '5%',
					align: 'center',
					accessor: 'grossRent',
					cellAlign: 'center',
					Header: 'Gross Pay',
				},
				{
					id: 'advanceAmount',
					width: '5%',
					align: 'center',
					accessor: 'advanceAmount',
					cellAlign: 'center',
					Header: 'Advance',
				},
				{
					id: 'netPay',
					width: '5%',
					align: 'center',
					accessor: 'netPay',
					cellAlign: 'center',
					Header: 'Net Pay',
				},

				{
					id: 'ibr',
					width: '5%',
					align: 'center',
					accessor: 'ibr',
					cellAlign: 'center',
					Header: 'IBR (%)',
				},

				{
					id: 'discountFactor',
					width: '5%',
					align: 'center',
					accessor: 'discountFactor',
					cellAlign: 'center',
					Header: 'Discount Factor',
				},

				{
					id: 'rentExp',
					width: '5%',
					align: 'center',
					accessor: 'rentExp',
					cellAlign: 'center',
					Header: 'PV of Rent',
				},

				{
					id: 'openLeaseLiability',
					width: '5%',
					align: 'right',
					accessor: 'openLeaseLiability',
					cellAlign: 'right',
					Header: 'Open Lease Liability',
				},
				{
					id: 'adjustment',
					width: '5%',
					align: 'right',
					accessor: 'adjustment',
					cellAlign: 'right',
					Header: 'Adjustment',
				},
				{
					id: 'interestExp',
					width: '5%',
					align: 'right',
					accessor: 'interestExp',
					cellAlign: 'right',
					Header: 'Interest Expense',
				},
				{
					id: 'payment',
					width: '5%',
					align: 'right',
					accessor: 'payment',
					cellAlign: 'right',
					Header: 'Payment',
				},

				{
					id: 'closingLeaseLiability',
					width: '5%',
					align: 'right',
					accessor: 'closingLeaseLiability',
					cellAlign: 'right',
					Header: 'Closing Lease Liability',
				},
				{
					id: 'principal',
					width: '5%',
					align: 'right',
					accessor: 'principal',
					cellAlign: 'right',
					Header: 'Principal',
				},
				{
					id: 'cllfye',
					width: '5%',
					align: 'right',
					accessor: 'cllfye',
					cellAlign: 'right',
					Header: 'Current Lease Liability Financial Year End',
				},
				{
					id: 'cllr12',
					width: '5%',
					align: 'right',
					accessor: 'cllr12',
					cellAlign: 'right',
					Header: 'Current Lease Liability (Rolling 12)',
				},
				{
					id: 'ncllfye',
					width: '5%',
					align: 'right',
					accessor: 'ncllfye',
					cellAlign: 'right',
					Header: 'Non-Current Lease Liability Financial Year End',
				},
				{
					id: 'ncllr12',
					width: '5%',
					align: 'right',
					accessor: 'ncllr12',
					cellAlign: 'right',
					Header: 'Non-Current Lease Liability (Rolling 12)',
				},
				...(isActionColumnShowAllow
					? [
							{
								Header: 'Action',
								Cell: (cell) => {
									// Only show the Action Required button when needed
									if (cell?.row?.original?.processStatus?.toLowerCase() === 'returned') {
										return (
											<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
												<div style={{ textAlign: 'center' }}>
													<Tooltip title="Action Required">
														<Button
															type="text"
															icon={<Notification01Icon width={16} height={16} color="#B00020" />}
															onClick={(e) => {
																e.stopPropagation();
																setRowId(cell?.row?.original?._id);
																setActionRequiredConfirmModal(true);
															}}
														></Button>
													</Tooltip>
												</div>
											</Box>
										);
									}
									// Return null if no action is needed
									return null;
								},
								disableSortBy: 'true',
								align: 'center',
							},
						]
					: []),
			];
		}
	}, [isActionColumnShowAllow, leaseType]);

	const tableData = Array.isArray(leaseControlData)
		? leaseControlData?.map((item, index) => ({
				...item,
				date: dayjs(item.date).format('YYYY-MM-DD'),
				id: index,
				period: item?.period,
				grossRent: formatNumber(item.grossRent, 2),
				discountFactor: formatNumber(item.discountFactor, 4),
				rentExp: formatNumber(item.rentExp, 2),
				interestExp: formatNumber(item.interestExp, 2),
				openLeaseLiability: formatNumber(item.openLeaseLiability, 2),
				interest: formatNumber(item.interest, 2),
				payment: formatNumber(item.payment, 2),
				closingLeaseLiability: formatNumber(item.closingLeaseLiability, 2),
				adjustment: formatNumber(item.adjustment, 2),
				advanceAmount: formatNumber(item.advanceAmount, 2),
				netPay: formatNumber(item.netPay, 2),
				ibr: formatNumber(item.ibr, 2),
				principal: formatNumber(item.principal, 2),
				cllfye: formatNumber(item.cllfye, 2),
				cllr12: formatNumber(item.cllr12, 2),
				ncllfye: formatNumber(item.ncllfye, 2),
				ncllr12: formatNumber(item.ncllr12, 2),
			}))
		: [];

	const ModalData = {
		head: 'Returned Lease - Required Action',
	};

	const rowIndexColorMapping = useMemo(() => {
		const statusColorMap = {
			Pending: '#E6F0FF', // light blue
			// Pending: 'red', // light blue
			Unprocessed: '#E6F0FF', // light blue
			Reversed: '#ffcccc', // light red
			Returned: '#FFFDE6', // light yellow
			Recorded: '#E6FFE6', // light green
			Processed: '#E6F0FF', // light blue
		};

		let newMapping = {};

		Array.isArray(leaseControlData) &&
			leaseControlData?.forEach((lease, index) => {
				if (lease.processStatus) {
					if (lease.processStatus.toLowerCase() === 'returned') {
						setIsActionColumnShowAllow(true);
					}

					if (!isEmpty(viewData?.refCCAssignmentIdentityId) && !isEmpty(viewData?.refCCAssignmentIdentityId)) {
						newMapping[index] = {
							rowColor: statusColorMap[lease.processStatus] || '#FCFCFC',
						};
					}
				}
			});

		return newMapping;
	}, [leaseControlData]);

	return (
		<>
			{Array.isArray(tableData) && tableData?.length !== 0 && (
				<DataTable
					pagination={false}
					DATA={tableData || []}
					firstColStrick={false}
					COLUMNS={columnData || []}
					pageSize={tableData.length}
					xlsxFileName={`${leaseId}_date`}
					rowIndexColorMapping={rowIndexColorMapping}
					onClickHandler={(row) => handleEyeButton(row)}
					defaultVisibleColumn={['principal', 'cllfye', 'cllr12', 'ncllfye', 'ncllr12']}
				/>
			)}

			{showJournalEntryModal && (
				<LeaseV9JournalModal
					showModal={showJournalEntryModal}
					setShowModal={setShowJournalEntryModal}
					isDataLoading={isJournalEntryDataLoading}
					tableUpperLeftExtraInfo={tableUpperLeftExtraInfo}
					checkResponseData={journalEntryData?.responseData || []}
				/>
			)}

			{actionRequiredConfirmModal && (
				<ConfirmModal
					modalData={ModalData}
					showModal={actionRequiredConfirmModal}
					handleSubmit={handleActionRequiredButton}
					setShowModal={setActionRequiredConfirmModal}
				/>
			)}
		</>
	);
};

export default LeaseV9NewTrailModeScheduleTable;
